import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Injector,
  OnInit,
  Output
} from '@angular/core';
import { CardChartActionsComponent } from 'app/charts-dashboard/components/card-chart/actions/card-chart-actions.component';
import { ChartsCommonService } from 'app/charts-dashboard/services/charts-common.service';
import { locale } from 'app/charts-dashboard/components/card-chart/tabs/locale';
import { AppStore } from '@iv/store/app.store';
import * as Redux from 'redux';
import { AppState } from '@iv/store/app.reducer';
import {
  ChartFilterPatternNameEnum,
  ChartTypeEnum,
  CustomControlsNameEnum,
  IChartFilter,
  IChartFilterPattern,
  ISelectedFilters,
  Pattern
} from 'app/charts-dashboard/interfaces';
import {
  AbstractControl,
  FormControl,
  FormGroup
} from '@angular/forms';
import {
  debounceTime,
  distinctUntilChanged,
  skip,
  takeUntil
} from 'rxjs/operators';
import { IChartSeries } from 'app/charts-dashboard/state/series/chart-series.model';
import { ChartsDialogsService } from 'app/charts-dashboard/services/charts-dialogs.service';
import { ChartsDashboardQuery } from 'app/charts-dashboard/state/dashboard';
import { ChartsDashboardStateService } from 'app/charts-dashboard/state/charts-dashboard-state.service';
import { ChartsDashboardCardQuery } from 'app/charts-dashboard/state/card';
import { ChartsDashboardSeriesQuery } from 'app/charts-dashboard/state/series';
import {
  EntityAction,
  EntityActions,
  ID
} from '@datorama/akita';
import { Subscription } from 'rxjs';

interface IListValuesInTabs {
  [id: string]: { [key: string]: IChartFilterPattern<Pattern>[] };
}

@Component({
  selector: 'iv-live-card-chart-tabs',
  templateUrl: './card-chart-tabs.component.html',
  styleUrls: ['./card-chart-tabs.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CardChartTabsComponent extends CardChartActionsComponent implements OnInit {

  constructor(
    private readonly dashboardQuery: ChartsDashboardQuery,
    private readonly cardsQuery: ChartsDashboardCardQuery,
    private readonly changeDetectorRef: ChangeDetectorRef,
    protected readonly chartsCommonService: ChartsCommonService,
    @Inject(AppStore) protected readonly store: Redux.Store<AppState>,
    @Inject(Injector) protected readonly injector: Injector,
    protected readonly chartsDialogsService: ChartsDialogsService,
    protected readonly chartsState: ChartsDashboardStateService,
    protected readonly seriesQuery: ChartsDashboardSeriesQuery
  ) {
    super(
      chartsCommonService,
      store,
      injector,
      chartsDialogsService,
      chartsState,
      seriesQuery
    );

    this.dashboardQuery.selectDashboard(this.dashboardQuery.getCurrentDashboardId)
      .pipe(
        skip(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        const selectedFilters = JSON.parse(JSON.stringify(this.mainSeries.selectedFilters))
        delete selectedFilters?.timeRange;
        delete selectedFilters?.eodTimeRange;
        delete selectedFilters?.realtime;
        delete selectedFilters?.vModeToggler;
        this.form.controls[this.mainSeries.id].setValue(selectedFilters, {emitEvent: false});
      });
  }

  @Output()
  private skipSettingTimestamp = new EventEmitter<boolean>();

  public form = new FormGroup({});
  public locale = locale;
  public listsValues: IListValuesInTabs;
  public series: IChartSeries[] = [];

  private tabSubscriptions: { [tabId: string]: Subscription } = {};

  public ngOnInit(): void {
    this.seriesSubscription();
    this.initTabsForms();
  }

  public deleteCompareSeries(parentCardId: string, compareSeriesId: string, color: string): void {
    this.chartsState.deleteSeries(compareSeriesId);
    this.chartsState.updateCard(parentCardId, {
      chartSeries: this.cardsQuery.getEntity(parentCardId).chartSeries.filter(id => id !== compareSeriesId)
    });
    this.chartsState.restoreColorToDefaults(color,parentCardId)
    this.chartsState.updateDashboard();
    this.form.removeControl(compareSeriesId);
  }

  public close(tabElement): void {
    if (tabElement.checked) {
      tabElement.checked = false;
    }
  }

  public getSeriesSymbol(seriesId: ID): string {
    return this.seriesQuery.getEntity(seriesId)?.stock.symbol;
  }

  public getIVDiffSymbol(seriesId: ID, point: 'first' | 'second'): string {
    return this.seriesQuery.getEntity(seriesId)?.selectedFilters[`stockId-${point}`].symbol;
  }

  private initTabsForms(): void {
    this.series = this.seriesQuery.getSeriesFromCard(this.cardsQuery.getEntity(this.card.id));
    this.series.forEach((series: IChartSeries) => {
      this.form.addControl(series.id.toString(), new FormGroup(this.addFilterControlsToTab(series)));
      this.valueChanges(series);
    });
    this.changeDetectorRef.detectChanges();
  }

  private valueChanges(series: IChartSeries): void {
    this.tabSubscriptions[series.id] = new Subscription();
    this.tabSubscriptions[series.id].add(this.form.controls[series.id].valueChanges
      .pipe(
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((value: ISelectedFilters) => {
        const selectedFilters: ISelectedFilters = {};

        for (const key of Object.keys(value)) {
          selectedFilters[key] = value[key];
        }

        if (this.chartsCommonService.isMaturity(this.card.chartId)) {
          this.skipSettingTimestamp.emit(
            selectedFilters[ChartFilterPatternNameEnum.DATE_TIME] === this.seriesQuery.getEntity(series.id).selectedFilters.datetime
          );
        }

        this.chartsState.updateSelectedFilters(this.card, series.id, selectedFilters);
      })
    );
  }

  private addFilterControlsToTab(series: IChartSeries): { [name: string]: AbstractControl } {
    const tabFilters = {};
    const newListsValues: IListValuesInTabs = {};

    Object.keys(series.selectedFilters).forEach((key: string) => {
      if (
        key === ChartFilterPatternNameEnum.TIME_RANGE ||
        key === ChartFilterPatternNameEnum.EOD_TIME_RANGE ||
        key === ChartFilterPatternNameEnum.REALTIME ||
        key === ChartFilterPatternNameEnum.STOCK_ID ||
        key === CustomControlsNameEnum.V_MODE_TOGGLER
      ) return;

      tabFilters[key] = new FormControl(series.selectedFilters[key]);

      if (this.card.chartId === "SINGLE_CONTRACT_ANALYZER" && series.filters) {
        newListsValues[series.id] = {
          ...newListsValues[series.id],
          [key]: series.filters.find((filterItem: IChartFilter) => filterItem.parameterId === key)?.pattern
        };

      } else {
        newListsValues[series.id] = {
          ...newListsValues[series.id],
          [key]: this.cardsQuery.getEntity(this.card.id).filters.find((filterItem: IChartFilter) => filterItem.parameterId === key)?.pattern
        };
      }
    });

    this.listsValues = {...this.listsValues, ...newListsValues};

    return tabFilters;
  }

  private seriesSubscription(): void {
    this.seriesQuery.selectEntityAction([EntityActions.Add, EntityActions.Update, EntityActions.Remove])
      .pipe(
        debounceTime(1),
        takeUntil(this.destroy$)
      )
      .subscribe((action: EntityAction<ID>) => {
        switch (action.type) {
          case EntityActions.Add:
            this.series = this.seriesQuery.getSeriesFromCard(this.cardsQuery.getEntity(this.card.id));
            action.ids.forEach((id: ID) => {
              if (id !== Object.keys(this.form.controls)[id]) {
                this.form.addControl(id.toString(), new FormGroup(this.addFilterControlsToTab(this.seriesQuery.getEntity(id))));
                this.valueChanges(this.seriesQuery.getEntity(id));
              }
            });
            break;
          case EntityActions.Update:
            if (action.ids.filter(id => this.card.chartSeries.includes(id)).length) {
              const series: IChartSeries = this.seriesQuery.getEntity(action.ids[0]);

              if (this.isMaturity) {
                this.form
                  .get(action.ids[0].toString())
                  .get(ChartFilterPatternNameEnum.DATE_TIME)
                  ?.setValue(series.selectedFilters.datetime, {emitEvent: false});
              }

              if (this.card.chartId === ChartTypeEnum.SINGLE_CONTRACT_ANALYZER) {
                action.ids.forEach((id: ID) => {
                  this.setSelectedFiltersToControls(id)
                });
              }

              if (this.card.chartId === ChartTypeEnum.IV_DIFFERENCE) {
                this.form.controls[action.ids[0]]
                  .get(`${ChartFilterPatternNameEnum.STOCK_ID}-first`)
                  .setValue(series.selectedFilters['stockId-first'], {emitEvent: false});
                this.form.controls[action.ids[0]]
                  .get(`${ChartFilterPatternNameEnum.STOCK_ID}-second`)
                  .setValue(series.selectedFilters['stockId-second'], {emitEvent: false});
              }
            }
            break;
          case EntityActions.Remove:
            this.series = this.seriesQuery.getSeriesFromCard(this.cardsQuery.getEntity(this.card.id));
            action.ids.forEach((id: ID) => {
              if (id !== Object.keys(this.form.controls)[id]) {
                this.form.removeControl(id.toString());
                this.tabSubscriptions[id]?.unsubscribe();
              }
            });
            break;
        }
        this.changeDetectorRef.detectChanges();
      });
  }

  private setSelectedFiltersToControls(id: ID): void {
    this.addFilterControlsToTab(this.seriesQuery.getEntity(id));
    const selectedFilters = this.seriesQuery.getEntity(id).selectedFilters;
    for(let filter of Object.keys(selectedFilters)) {
      this.form.controls[id].get(filter)?.setValue(selectedFilters[filter], {emitEvent: false})
    }
  }
}
